import { useParams } from '@reach/router';
import { graphql } from 'gatsby';
import React, { FC } from 'react';

import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  Copy,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import {
  DocumentDownloadIcon,
  NextIcon,
  PreviousIcon,
  RefreshIcon,
} from '@/components/icons';
import { useTranslate } from '@/contexts';
import { PlayerInformationHistoryEventType, Service } from '@/globalTypes';
import { assert } from '@/utils/error';
import useGlobalPlayerInformationHistory from './useGlobalPlayerInformationHistory';
import useGlobalPlayerInformationHistoryReport from './useGlobalPlayerInformationHistoryReport';

export const Fragment = graphql`
  fragment SanityGlobalPlayerInformationHistoryBlock on SanityGlobalPlayerInformationHistoryBlock {
    title {
      ...SanityLocaleString
    }
  }
`;

const GlobalPlayerInformationHistoryBlock: FC<{
  block: Queries.SanityGlobalPlayerInformationHistoryBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const { playerGlobalId } = useParams();

  assert(playerGlobalId, 'missing playerGlobalId');

  const {
    entries,
    fetching,
    nextPage,
    previousPage,
    refresh,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
  } = useGlobalPlayerInformationHistory(playerGlobalId);
  const report = useGlobalPlayerInformationHistoryReport(playerGlobalId);

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'globalPlayerInformationHistory',
    data: entries,
    fetching,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.dateTimeValue({
        field: 'timestamp',
        title: 'Time',
        fromFilterField: 'from',
        toFilterField: 'to',
      }),
      s.stringValue({
        field: 'eventId',
        title: 'Event ID',
        getValue({ row }) {
          return <Copy value={row.eventId} />;
        },
      }),
      s.stringValue({
        field: 'playerUUID',
        title: 'Player UUID',
        filterField: 'playerUUID',
        getValue({ row }) {
          return <Copy value={row.playerUUID} />;
        },
      }),
      s.enumValue({
        field: 'service',
        title: 'Service',
        e: Service,
        filterField: 'service',
        getValue({ row }) {
          return <Copy value={row.service} />;
        },
      }),
      s.initiatorValue({
        field: 'initiator',
        title: 'Initiator',
      }),
      s.enumValue({
        field: 'eventType',
        title: 'Event',
        e: PlayerInformationHistoryEventType,
        filterField: 'eventType',
        getValue({ row }) {
          return <Copy value={row.eventType} />;
        },
      }),
      s.stringValue({
        field: 'value',
        title: 'Details',
      }),
    ],
  });

  if (!block) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={report.loading}
            onClick={() => report.generate()}
          >
            <DocumentDownloadIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default GlobalPlayerInformationHistoryBlock;
